import { Anchor, Box, Center, ScrollArea, Space, Table, Text } from '@mantine/core';
import { Trans, useTranslation } from 'react-i18next';
import { CenteredLoading } from 'components/CenteredLoading';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { Column, Row, TableAction, TableActionConfig } from 'utils/models';
import { displayNotificationError } from 'utils/notifications';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from 'context/hooks';
import { TableConfig, TableInput } from 'models/general';
import { useElementSize, useViewportSize } from '@mantine/hooks';
import { DEFAULT_LIST_REFETCH_INTERVAL } from 'utils/query';
import { PaperWithBorder } from 'components/PaperWithBorder';
import { uniqBy } from 'lodash';
import { PagedResult } from '@extrahorizon/javascript-sdk';
import { TableRow } from './TableRow';
import { TableHeader } from '../TableHeader/TableHeader';
import { TableActions } from './TableActions';
import classes from '../Table.module.css';

interface RequiredTableData {
  id: string;
}

interface Props<T extends RequiredTableData> {
  getData: (input: TableInput) => Promise<PagedResult<T>>;
  getColumns: () => Column<T>[];
  getRow?: () => Row<T>;
  filterBar?: React.ReactNode;
  titleBar?: React.ReactNode;
  limit?: number;
  loadToInfinity?: boolean;
  config: TableConfig;
  selectableRowId?: (item?: T) => string | undefined;
  selectedFilter?: (item: T) => boolean;
  selectedActions?: () => TableActionConfig;
  h?: number;
  scrollableAfterNrItems?: number;
  emptyLabel?: string;
  saveFiltersForLater?: boolean;
}

export const TableBody = <T extends RequiredTableData>({
  getData,
  getColumns,
  getRow,
  filterBar,
  titleBar,
  limit,
  loadToInfinity = true,
  config,
  selectableRowId,
  selectedFilter,
  selectedActions,
  h,
  scrollableAfterNrItems = 3,
  emptyLabel = 'no_search_results_label',
  saveFiltersForLater = true,
}: Props<T>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { queryKey } = config;
  const { parameters: filters } = useSearchParameters(queryKey, saveFiltersForLater);
  const { currentUser, selectedTableRowIds, updateTableRowIds } = useAppContext();
  const params = useParams();
  const { ref: scrollContainerRef, height: scrollContainerHeight } = useElementSize();
  const { height: viewPortHeight } = useViewportSize();

  const activeGroups = currentUser?.visibleGroups.map((group) => group.id) || [];
  const hasActiveGroups = !!(activeGroups && activeGroups.length > 0);

  const { data, fetchNextPage, isSuccess, isError, isPending, hasNextPage } = useInfiniteQuery({
    queryKey: [queryKey, filters],
    queryFn: ({ pageParam }) => {
      return getData({
        pageParam,
        filters,
        currentUser,
        config,
        limit,
        params,
      });
    },
    refetchInterval: config.refetchInterval || DEFAULT_LIST_REFETCH_INTERVAL,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (loadToInfinity) {
        const { page } = lastPage;
        if (page?.total > page?.offset + page?.limit) {
          return page?.offset / page?.limit + 1;
        }
      }
      return undefined;
    },
  });

  const dataLength = (data?.pages || []).reduce((previousCount, page) => previousCount + page.data?.length, 0);
  const allData = uniqBy(data?.pages.flatMap((page) => page.data) ?? [], 'id');
  const allRowIds = allData.map((item) => item?.id);

  const columns = getColumns();

  const selectable = selectableRowId != null;
  let isUpdatingRecords = false;
  let actions: TableAction[] = [];
  let labelIsUpdating = '';
  if (selectedActions) {
    const { tableActions, isUpdating, isUpdatingLabel } = selectedActions();
    isUpdatingRecords = isUpdating;
    actions = tableActions;
    labelIsUpdating = isUpdatingLabel;
  }

  const filteredSelectedIds = () => {
    if (selectedFilter) {
      const filteredList: string[] = allData
        .filter(selectedFilter)
        .map((item) => item.id)
        .filter((id) => selectedTableRowIds?.includes(id));
      return filteredList;
    }
    return selectedTableRowIds || [];
  };

  useEffect(() => {
    return () => updateTableRowIds?.([]);
  }, [updateTableRowIds]);

  useEffect(() => {
    const shouldLoadDataIfScreenIsNotFullWithData = async () => {
      if (!isPending && hasNextPage && scrollContainerHeight > 0 && scrollContainerHeight < viewPortHeight) {
        await fetchNextPage();
      }
    };
    shouldLoadDataIfScreenIsNotFullWithData().catch(() => displayNotificationError());
  }, [isPending, fetchNextPage, scrollContainerHeight, hasNextPage, viewPortHeight]);

  const renderTableAction = () => {
    if (selectable && actions) {
      return <TableActions actions={actions} selectedRows={selectedTableRowIds} selectedIds={filteredSelectedIds} />;
    }
    return <Space h={'md'} />;
  };

  return (
    <PaperWithBorder testId={`${queryKey}-table-body`}>
      {titleBar}
      <Box className={classes.tableHeader}>
        {filterBar}
        {renderTableAction()}
        <TableHeader<T>
          columns={columns}
          config={config}
          selectable={selectable}
          allRowIds={allRowIds}
          testId={`${queryKey}-table-filters`}
        />
      </Box>
      <InfiniteScroll dataLength={dataLength} next={fetchNextPage} hasMore={!!hasNextPage} loader={null}>
        <Box>
          {!isUpdatingRecords && (
            <ScrollArea h={h && allData?.length > scrollableAfterNrItems ? h : undefined} type={'auto'}>
              <Table className={classes.table} id={`${queryKey}ToInfinity`} ref={scrollContainerRef}>
                <tbody>
                  {allData.map((item: T, index: number) => (
                    <TableRow<T>
                      key={index}
                      columns={columns}
                      row={getRow && getRow()}
                      data={item}
                      rowId={(selectableRowId && selectableRowId(item)) || index.toString()}
                      testId={`${queryKey}-row`}
                      selectable={selectable}
                    />
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          )}
          {isUpdatingRecords && <CenteredLoading mt={'xs'} label={labelIsUpdating} />}
          {isSuccess && !hasActiveGroups && (
            <Center>
              <Text pt={'sm'} size={'sm'}>
                <Trans
                  i18nKey={'dashboard_no_active_alerts'}
                  components={[
                    <Anchor
                      key={'groupSettingsLinkKey'}
                      c={'var(--mantine-color-blue-7)'}
                      onClick={() => navigate('/settings')}
                    />,
                  ]}
                  values={{ value: t('navbar_settings') }}
                />
              </Text>
            </Center>
          )}
          {isSuccess && hasActiveGroups && dataLength === 0 && <Center mt={'xs'}>{t(emptyLabel)}</Center>}
          {isPending && <CenteredLoading mt={'xs'} />}
          {isError && <Center mt={10}>{t('api_error_refr')}</Center>}
        </Box>
      </InfiniteScroll>
    </PaperWithBorder>
  );
};
